(function (root) {
  'use strict';

  // Gift presentation and preparation metadata only. Never add a gift variant.
  function giftRemovals(cart, config) {
    const handles = new Set(config.giftHandles);
    return Object.fromEntries((cart.items || [])
      .filter((item) => handles.has(item.handle) && typeof item.key === 'string' && item.key.length > 0)
      .map((item) => [item.key, 0]));
  }

  function evaluateCampaigns(cart, config, time) {
    const gifts = new Set(config.giftHandles);
    return config.campaigns.map((campaign) => {
      const active = time >= Date.parse(campaign.startsAt) && time < Date.parse(campaign.endsAt);
      const handles = new Set(campaign.qualifyingHandles);
      const quantity = active ? (cart.items || []).reduce((total, item) => {
        const count = Number(item.quantity);
        return handles.has(item.handle) && !gifts.has(item.handle) && Number(item.final_line_price) > 0 && Number.isInteger(count) && count > 0
          ? total + count : total;
      }, 0) : 0;
      const unitsPerGift = Number(campaign.minimumPaidUnits);
      const giftCount = active && campaign.giftQuantityRule === 'per_complete_paid_group' && Number.isInteger(unitsPerGift) && unitsPerGift > 0
        ? Math.floor(quantity / unitsPerGift) : 0;
      return { campaign, active, quantity, giftCount, eligible: giftCount > 0 };
    });
  }

  function preparationValue(states) {
    return states.filter((state) => state.eligible)
      .map((state) => `${state.campaign.giftTitle} (${state.giftCount} kpl), ${state.campaign.id}`).join('; ');
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { giftRemovals, evaluateCampaigns, preparationValue };
  if (!root.document) return;
  const configElement = document.getElementById('gift-campaign-config');
  if (!configElement) return;
  let config;
  let lastStates = [];
  let lastCart = null;
  let revision = 0;
  let timer;
  let pending = false;
  let running = false;
  let queued = false;
  const startedAt = performance.now();
  const serverTime = Number(configElement.dataset.serverTime) * 1000;
  const currentTime = () => serverTime + performance.now() - startedAt;
  const journal = [];
  // Inspectable local runtime journal; no analytics or external destination.
  root.NettiapteekkiGiftJournal = journal;
  function log(event, details) {
    journal.push({ time: new Date(currentTime()).toISOString(), event, ...details });
    if (journal.length > 50) journal.shift();
  }

  function render() {
    document.querySelectorAll('gift-campaign-notice').forEach((element) => {
      element.replaceChildren();
      const handle = element.dataset.productHandle;
      const relevant = lastStates.filter((state) => state.active && (handle
        ? state.campaign.qualifyingHandles.includes(handle)
        : !pending && state.quantity > 0));
      element.hidden = relevant.length === 0;
      relevant.forEach((state) => {
        const box = document.createElement('div');
        box.className = 'gift-campaign-notice';
        if (state.campaign.giftImage) {
          const image = document.createElement('img');
          image.src = new URL(state.campaign.giftImage, new URL(configElement.dataset.configUrl, document.baseURI)).href;
          image.alt = state.campaign.giftTitle;
          image.width = 80;
          image.height = 80;
          image.loading = 'lazy';
          image.className = 'gift-campaign-notice__image';
          box.append(image);
        }
        const title = document.createElement('p');
        title.className = 'gift-campaign-notice__title';
        const link = document.createElement('a');
        link.href = state.campaign.url;
        link.textContent = state.campaign.title;
        title.append(link);
        box.append(title);
        const conditions = document.createElement('p');
        conditions.textContent = state.campaign.conditions;
        box.append(conditions);
        if (state.eligible && !pending) {
          const heading = document.createElement('p');
          heading.className = 'gift-campaign-notice__title';
          heading.textContent = state.giftCount > 1 ? 'Lahjat on huomioitu tilauksessasi' : 'Lahja on huomioitu tilauksessasi';
          box.append(heading);
          const quantity = document.createElement('p');
          quantity.className = 'gift-campaign-notice__quantity';
          quantity.textContent = `${state.giftCount} × ${state.campaign.giftTitle}`;
          box.append(quantity);
          const confirmation = document.createElement('p');
          confirmation.className = 'gift-campaign-notice__confirmation';
          confirmation.setAttribute('role', 'status');
          confirmation.textContent = state.giftCount > 1
            ? 'Lähetämme lahjat tilauksesi mukana. Lahjat eivät näy erillisinä tuotteina ostoskorissa. Voit viimeistellä tilauksesi normaalisti.'
            : 'Lähetämme lahjan tilauksesi mukana. Lahja ei näy erillisenä tuotteena ostoskorissa. Voit viimeistellä tilauksesi normaalisti.';
          box.append(confirmation);
        }
        element.append(box);
      });
    });
    const value = !pending ? preparationValue(lastStates) : '';
    document.querySelectorAll('form[action]').forEach((form) => {
      if (!Array.from(form.elements).some((control) => control.name === 'checkout')) return;
      let field = form.querySelector('[data-gift-preparation]');
      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.dataset.giftPreparation = '';
        field.name = `attributes[${config.informationalAttribute}]`;
        form.append(field);
      }
      field.value = value;
    });
  }

  async function readCart() {
    const response = await fetch(`${configElement.dataset.cartUrl}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(`cart-read-${response.status}`);
    return response.json();
  }

  async function updateCart(body) {
    const response = await fetch(configElement.dataset.updateUrl + '.js', {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`cart-update-${response.status}`);
    const cart = await response.json();
    if (cart.errors || !Array.isArray(cart.items)) throw new Error('cart-update-invalid');
    return cart;
  }

  function sectionDescriptors() {
    const descriptors = [];
    document.querySelectorAll('cart-items, cart-drawer').forEach((element) => {
      if (typeof element.getSectionsToRender !== 'function') return;
      try { descriptors.push(...element.getSectionsToRender()); } catch (_) { /* Detached cart section. */ }
    });
    return descriptors.filter((entry, index, entries) => entries.findIndex((other) => other.id === entry.id) === index);
  }

  async function refresh() {
    if (!config) return;
    if (running) { queued = true; return; }
    running = true;
    const token = revision;
    pending = true;
    render();
    try {
      let cart = await readCart();
      if (token !== revision) return;
      const updates = giftRemovals(cart, config);
      if (Object.keys(updates).length) {
        const descriptors = sectionDescriptors();
        cart = await updateCart({ updates, sections: descriptors.map((entry) => entry.section || entry.id), sections_url: location.pathname });
        log('gift-lines-removed', { lineCount: Object.keys(updates).length });
        descriptors.forEach((entry) => {
          const html = cart.sections && cart.sections[entry.section || entry.id];
          const target = entry.id === 'cart-drawer'
            ? document.querySelector(entry.selector)
            : document.getElementById(entry.id);
          if (!html || !target) return;
          const replacement = new DOMParser().parseFromString(html, 'text/html').querySelector(entry.selector || '.shopify-section');
          const destination = target.querySelector(entry.selector || '.shopify-section') || target;
          if (replacement) destination.innerHTML = replacement.innerHTML;
        });
        document.querySelectorAll('cart-drawer').forEach((drawer) => {
          drawer.classList.toggle('is-empty', cart.item_count === 0);
          const overlay = drawer.querySelector('#CartDrawer-Overlay');
          if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', () => drawer.close());
        });
        document.querySelectorAll('cart-items, #main-cart-footer').forEach((element) => element.classList.toggle('is-empty', cart.item_count === 0));
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-notice' });
      }
      if (token !== revision) return;
      lastCart = cart;
      lastStates = evaluateCampaigns(cart, config, currentTime());
      pending = false;
      render();
      const value = preparationValue(lastStates);
      if ((cart.attributes || {})[config.informationalAttribute] !== value) {
        try {
          await updateCart({ attributes: { [config.informationalAttribute]: value } });
          log('preparation-attribute-saved', { eligibleCampaigns: lastStates.filter((state) => state.eligible).length });
        } catch (error) {
          log('preparation-attribute-failed', { reason: error.message });
        }
      }
    } catch (error) {
      // A failed read/removal never becomes a positive confirmation or blocks checkout.
      lastStates = [];
      lastCart = null;
      pending = false;
      render();
      log('gift-refresh-failed', { reason: error.message });
    } finally {
      running = false;
      if (queued) { queued = false; schedule(); }
    }
  }

  function schedule() {
    revision += 1;
    pending = true;
    if (config) render();
    clearTimeout(timer);
    timer = setTimeout(refresh, 250);
  }

  customElements.define('gift-campaign-notice', class extends HTMLElement {
    connectedCallback() { if (config) render(); }
  });

  fetch(configElement.dataset.configUrl, { credentials: 'same-origin' })
    .then((response) => { if (!response.ok) throw new Error('gift-config-unavailable'); return response.json(); })
    .then((data) => {
      config = data;
      if (!Number.isFinite(serverTime) || serverTime <= 0) throw new Error('gift-server-time-unavailable');
      // Conditions may be shown on product pages before the cart has been read; confirmation may not.
      lastStates = evaluateCampaigns({ items: [] }, config, currentTime());
      schedule();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => { if (event.source !== 'gift-notice') schedule(); });
        subscribe(PUB_SUB_EVENTS.cartError, schedule);
      }
      document.addEventListener('change', (event) => {
        if (event.target.matches('cart-items input[name="updates[]"], cart-drawer-items input[name="updates[]"]')) {
          revision += 1;
          pending = true;
          render();
        }
      });
      document.addEventListener('submit', (event) => {
        if (event.target.matches('product-form form')) { revision += 1; pending = true; render(); }
      }, true);
      document.addEventListener('click', (event) => {
        if (event.target.closest('cart-remove-button')) { revision += 1; pending = true; render(); }
      }, true);
      document.addEventListener('shopify:section:load', schedule);
      root.addEventListener('pageshow', schedule);
      root.setInterval(() => {
        if (!lastCart || pending) return;
        const states = evaluateCampaigns(lastCart, config, currentTime());
        if (preparationValue(states) !== preparationValue(lastStates) || states.some((state, index) => state.active !== lastStates[index]?.active)) schedule();
      }, 1000);
    })
    .catch((error) => log('gift-initialization-failed', { reason: error.message }));
})(typeof window !== 'undefined' ? window : globalThis);
